import { useState, useEffect, useRef } from "react";
import { useFormPopup } from "../provider/FormPopupContext";
import { Api } from "../provider/firebase";
import { safeUrl } from "../provider/functions";

/* ─── inline style objects ───────────────────────────────── */
const S = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 99999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    background: "rgba(0,0,0,0.45)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
  },
  card: {
    width: "100%",
    maxWidth: 420,
    borderRadius: 18,
    background: "#18181b",
    boxShadow: "0 24px 64px rgba(0,0,0,0.6), 0 4px 16px rgba(0,0,0,0.4)",
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.08)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "16px 16px 14px",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },
  iconWrap: {
    width: 38,
    height: 38,
    minWidth: 38,
    borderRadius: 10,
    background: "linear-gradient(135deg,#f97316,#ea580c)",
    boxShadow: "0 4px 12px rgba(249,115,22,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  titleCol: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
  },
  title: {
    fontSize: 15,
    fontWeight: 700,
    color: "#f4f4f5",
    margin: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  desc: {
    fontSize: 12,
    color: "#a1a1aa",
    margin: "3px 0 0",
    lineHeight: 1.45,
    whiteSpace: "normal",
  },
  closeBtn: {
    width: 28,
    height: 28,
    minWidth: 28,
    borderRadius: "50%",
    border: "none",
    background: "rgba(255,255,255,0.08)",
    color: "#a1a1aa",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    padding: 0,
  },
  body: {
    padding: "16px 16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    color: "#71717a",
    letterSpacing: "0.4px",
    textTransform: "uppercase",
  },
  input: {
    width: "100%",
    padding: "10px 13px",
    borderRadius: 10,
    border: "1.5px solid rgba(255,255,255,0.1)",
    background: "rgba(255,255,255,0.06)",
    color: "#f4f4f5",
    fontSize: 15,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    WebkitAppearance: "none",
  },
  inputFocus: {
    borderColor: "#f97316",
    background: "rgba(255,255,255,0.09)",
    boxShadow: "0 0 0 3px rgba(249,115,22,0.18)",
  },
  btn: {
    width: "100%",
    padding: "13px 18px",
    borderRadius: 11,
    border: "none",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 15,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    background: "linear-gradient(135deg,#f97316,#ea580c)",
    color: "#fff",
    boxShadow: "0 6px 20px rgba(249,115,22,0.35)",
  },
  btnDisabled: { opacity: 0.55, cursor: "not-allowed" },
  spinner: {
    width: 18,
    height: 18,
    border: "2px solid rgba(255,255,255,0.35)",
    borderTopColor: "#fff",
    borderRadius: "50%",
  },
  errorPill: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 13px",
    borderRadius: 10,
    background: "rgba(239,68,68,0.12)",
    border: "1px solid rgba(239,68,68,0.3)",
    color: "#fca5a5",
    fontSize: 13,
    fontWeight: 500,
  },
  successPill: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 13px",
    borderRadius: 10,
    background: "rgba(34,197,94,0.12)",
    border: "1px solid rgba(34,197,94,0.3)",
    color: "#86efac",
    fontSize: 13,
    fontWeight: 500,
  },
};

export default function FormPopup() {
  const { popup, openFormPopup, closeFormPopup } = useFormPopup();
  const { form = [], formTitle, formDescription } = popup;

  const [values, setValues]         = useState({});
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState("");
  const [focusedInput, setFocused]  = useState(null);
  const [countdowns, setCountdowns] = useState({});
  const firstInputRef               = useRef(null);
  const countdownRefs               = useRef({});

  useEffect(() => {
    if (popup.visible) {
      const initial = {};
      form.forEach((f) => { if (f.type !== "button") initial[f.name] = f.value ?? ""; });
      setValues(initial);
      setError("");
      setSuccess("");
      setLoading(false);
      setTimeout(() => firstInputRef.current?.focus(), 250);

      // Initialize countdowns for buttons that have a countdown property
      const initialCountdowns = {};
      form.forEach((f) => {
        if (f.type === "button" && f.countdown > 0) {
          initialCountdowns[f.name] = f.countdown;
        }
      });
      setCountdowns(initialCountdowns);

      // Clear any existing intervals
      Object.values(countdownRefs.current).forEach(clearInterval);
      countdownRefs.current = {};

      // Start countdown intervals
      form.forEach((f) => {
        if (f.type === "button" && f.countdown > 0) {
          countdownRefs.current[f.name] = setInterval(() => {
            setCountdowns((prev) => {
              const next = (prev[f.name] ?? 0) - 1;
              if (next <= 0) {
                clearInterval(countdownRefs.current[f.name]);
                delete countdownRefs.current[f.name];
                return { ...prev, [f.name]: 0 };
              }
              return { ...prev, [f.name]: next };
            });
          }, 1000);
        }
      });
    } else {
      // Cleanup intervals when popup closes
      Object.values(countdownRefs.current).forEach(clearInterval);
      countdownRefs.current = {};
      setCountdowns({});
      setLoading(false);
    }
  }, [popup.visible, form]);

  if (!popup.visible) return null;

  function handleClose() { closeFormPopup(); }

  function handleChange(name, val) {
    setValues((prev) => ({ ...prev, [name]: val }));
    setError("");
  }

  async function handleButtonClick(field) {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await Api.post(`/${field.value}`, { ...values });
      if (res.data?.form && Array.isArray(res.data.form)) {
        closeFormPopup();
        setTimeout(() => openFormPopup(res.data.form, res.data.formTitle, res.data.formDescription), 250);
      } else if (res.data?.error) {
        setError(res.data.error);
      } else if (res.data?.url) {
        // Sunucudan gelen yonlendirme adresi de sema denetiminden gecer (bkz. safeUrl).
        const target = safeUrl(res.data.url);
        if (target) window.location = target;
      } else if (res.data?.success) {
        setSuccess("İşlem başarılı!");
        setTimeout(() => handleClose(), 1400);
      } else {
        setError("Beklenmedik bir hata oluştu.");
      }
    } catch {
      setError("Bağlantı hatası, tekrar deneyiniz.");
    } finally {
      setLoading(false);
    }
  }

  const textFields   = form.filter((f) => f.type !== "button" && !f.hidden);
  const buttonFields = form.filter((f) => f.type === "button");

  return (
    <div style={S.overlay}>
      <div style={S.card} role="dialog" aria-modal="true">

        {/* HEADER */}
        <div style={S.header}>
          <div style={S.iconWrap}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
              <line x1="1" y1="10" x2="23" y2="10" />
            </svg>
          </div>

          <div style={S.titleCol}>
            <div style={S.title}>{formTitle || "Ödeme"}</div>
            {formDescription && (
              <div style={S.desc}>{formDescription}</div>
            )}
          </div>

          <button style={S.closeBtn} onClick={handleClose} aria-label="Kapat">
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none"
              stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M1 1l10 10M11 1L1 11" />
            </svg>
          </button>
        </div>

        {/* BODY */}
        <div style={S.body}>

          {textFields.map((field, idx) => (
            <div style={S.field} key={field.name}>
              <label style={S.label}>{field.label || field.name}</label>
              <input
                ref={idx === 0 ? firstInputRef : null}
                className="fp-input-dark"
                style={{
                  ...S.input,
                  ...(focusedInput === field.name ? S.inputFocus : {}),
                }}
                type={field.type === "email" || field.type === "number" ? field.type : "text"}
                placeholder={field.placeholder || ""}
                value={values[field.name] ?? ""}
                onChange={(e) => handleChange(field.name, e.target.value)}
                onFocus={() => setFocused(field.name)}
                onBlur={() => setFocused(null)}
                disabled={loading}
              />
            </div>
          ))}

          {error && (
            <div style={S.errorPill}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}

          {success && (
            <div style={S.successPill}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {success}
            </div>
          )}

          {buttonFields.map((field) => {
            const cd = countdowns[field.name] ?? 0;
            const isCountingDown = cd > 0;
            const isDisabled = loading || isCountingDown;
            return (
              <button
                key={field.name}
                style={{ ...S.btn, ...(isDisabled ? S.btnDisabled : {}) }}
                onClick={() => handleButtonClick(field)}
                disabled={isDisabled}
              >
                {loading ? (
                  <div style={{ ...S.spinner, animation: "fp-spin 0.65s linear infinite" }} />
                ) : isCountingDown ? (
                  <>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {field.label || field.value} ({cd})
                  </>
                ) : (
                  <>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                    {field.label || field.value}
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes fp-spin { to { transform: rotate(360deg) } }
        .fp-input-dark::placeholder { color: #52525b; }
        .fp-input-dark { color-scheme: dark; }
      `}</style>
    </div>
  );
}
